import dayjs from "dayjs";
import { mongoDBDateFormat } from "src/config/constants/genericConstants";
import {
  addToDate,
  getDateIndex,
  getFormatedDate,
  getPhyInvStartDate,
  getWeekStartAndEnd,
  getYearBefore,
  isDateOutsidePST,
  dateParamType
} from "src/utils/getDates";
import { IEowRequest, IRedirectRouteType } from "./interface";

/*
    Update start date condition
    if physical inventory submit date
        in week => next date of physical inventory submit date
        not in week => week start date
*/
export const getWeeklyReportStartDate = (
  weekEnding: dateParamType,
  phyInvDate: string[]
) => {
  // Get week Start Date
  let [weekStart]: string[] | number[] = getWeekStartAndEnd(
    weekEnding,
    mongoDBDateFormat
  );
  // Get last phsyical inventroy submit date
  const PhyInvStartDate = getPhyInvStartDate(weekStart, weekEnding, phyInvDate);
  if (PhyInvStartDate) {
    // get date index of the week (0 => sun, 1 => mon, 6 => sat and so on)
    const dateIndex = getDateIndex(PhyInvStartDate);
    const isSame = dayjs(PhyInvStartDate).isSame(weekEnding);
    // if not sat or sun then added 1 day to date
    if (!(dateIndex === 6 || isSame)) {
      weekStart = addToDate(PhyInvStartDate, 1, "day");
    }
  }
  return getFormatedDate(weekStart);
};

export const isExpandTrue = (data: any) => {
  return data.map((item: any) => {
    return {
      ...item,
      isExpand: true
    };
  });
};

export const searchInInnerData = (data: any, searchTerm: string) => {
  return data.map((item: any) => {
    if (Array.isArray(item?.innerData)) {
      const filteredInnerData = item.innerData.filter(
        (innerItem: any) =>
          innerItem?.docId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          innerItem?.notes
            ?.join(" ")
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
      );

      return {
        ...item,
        innerData: filteredInnerData,
        isExpand: true
      };
    }

    // If item.innerData does not exist or is not an array
    return {
      ...item,
      innerData: null
    };
  });
};

export const filterInnerData = (data: any) => {
  return data.filter((item: any) => {
    return (
      item?.highlighted ||
      (Array.isArray(item?.innerData) && item.innerData.length > 0)
    );
  });
};

export const isValidEndOfWeekDate = (date: string) =>
  isDateOutsidePST(dayjs(date).format("MM/DD/YYYY"));

export const isEndofWeekButtonDisable = (
  eowReport: { eowApiStatus: string; eowButtonStatus: boolean },
  request: { weekEndingDate: string }
) => {
  if (eowReport.eowApiStatus !== "idle") return true;
  return (
    isValidEndOfWeekDate(request.weekEndingDate) || eowReport.eowButtonStatus
  );
};

export const createRequestPayloadEow = (
  apiType: string,
  request: IEowRequest,
  type?: string
) => {
  const { weekStartingDate, weekEndingDate, storeId, createdBy } = request;
  const startDate = getFormatedDate(weekStartingDate, mongoDBDateFormat);
  const endDate = getFormatedDate(weekEndingDate, mongoDBDateFormat);
  const payload = { storeId };
  if (apiType === "eowsFinalized") {
    return {
      ...payload,
      weekEndingDate: endDate
    };
  }
  if (apiType === "eowDocumentList") {
    return {
      ...payload,
      startDate,
      endDate
    };
  }
  if (apiType === "eowSubmit") {
    return {
      ...payload,
      reportStartDate: startDate,
      reportEndDate: endDate,
      createdBy,
      response: type
    };
  }
};

export const underLineWords = {
  IN_TRANSIT: "In Transit",
  ON_ORDER: "On Order",
  THROWAWAYS: "Throwaways",
  MARKDOWNS: "Markdowns",
  PRICE_CHANGES: "Price Changes",
  MANUAL_TRANSFER: "Manual Transfers"
};

export const getRoutes = (
  processedName: string,
  matchedWord: string,
  request: IEowRequest
) => {
  if (!processedName) return { url: "", search: {} };

  const commonWords = [
    underLineWords.THROWAWAYS,
    underLineWords.MARKDOWNS,
    underLineWords.PRICE_CHANGES
  ];

  const isCommonWord = commonWords.includes(processedName);
  let status;

  if (isCommonWord || processedName === underLineWords.MANUAL_TRANSFER) {
    status = "Submitted";
  } else if (matchedWord === underLineWords.IN_TRANSIT) {
    status = "Stock in Transit";
  } else {
    status = "On Order";
  }

  const fromDate = isCommonWord
    ? request.weekStartingDate
    : getYearBefore(request.weekEndingDate, 1);

  const type = [
    underLineWords.MANUAL_TRANSFER,
    underLineWords.MARKDOWNS,
    underLineWords.THROWAWAYS,
    underLineWords.PRICE_CHANGES
  ].includes(processedName)
    ? processedName.slice(0, -1)
    : processedName;

  const route: IRedirectRouteType = {
    url: "/receive",
    search: {
      type,
      fromDate,
      toDate: request.weekEndingDate,
      ...{ status }
    }
  };

  return route;
};

export const getColour = ({ name, value }: { name: string; value: number }) => {
  if (
    name === underLineWords.PRICE_CHANGES ||
    name === underLineWords.THROWAWAYS ||
    name === underLineWords.MARKDOWNS
  ) {
    return value > 0 ? "green" : "error";
  } else {
    return value > 0 ? "error" : "green";
  }
};

export const getName = (processedName: string) => {
  const nameMap: Record<string, string> = {
    Transfer: "Transfers",
    DSD: "DSD(s)"
  };

  return processedName ? nameMap[processedName] || processedName : "";
};

export const getMatchedWord = (name: string) => {
  const regex = new RegExp(
    `\\b(${Object.values(underLineWords).join("|")})\\b`,
    "g"
  );
  const match = regex.exec(name);
  return { matchedWord: match ? match[0] : null, regex };
};
